import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';

export default function BuddyPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [buddyCode, setBuddyCode] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('buddy_code')
        .eq('id', user.id)
        .maybeSingle();
      setBuddyCode(profile?.buddy_code || '');

      const { data: conns } = await supabase
        .from('buddy_connections')
        .select('id, requester_id, receiver_id, status, created_at')
        .or(`requester_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('created_at', { ascending: false });
      setConnections(conns || []);

      const { data: profs } = await supabase.rpc('get_buddy_profiles');
      const map = {};
      (profs || []).forEach(p => { map[p.id] = p.full_name; });
      setProfiles(map);
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const accepted = connections.filter(c => c.status === 'accepted');
  const incoming = connections.filter(c => c.status === 'pending' && c.receiver_id === user?.id);
  const outgoing = connections.filter(c => c.status === 'pending' && c.requester_id === user?.id);

  function buddyName(conn) {
    const otherId = conn.requester_id === user.id ? conn.receiver_id : conn.requester_id;
    return profiles[otherId] || t('buddies.unknown');
  }

  async function handleRequest(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setSending(true);
    try {
      const { data, error } = await supabase.rpc('request_buddy', { p_code: code.trim() });
      if (error) throw error;
      if (data.error) {
        showError(t(`buddies.error_${data.error}`) || t('buddies.errorGeneric'));
      } else {
        showSuccess(t('buddies.requestSent'));
        setCode('');
        await load();
      }
    } catch (e) {
      showError(e.message);
    } finally {
      setSending(false);
    }
  }

  async function handleAccept(id) {
    try {
      const { data, error } = await supabase.rpc('accept_buddy_request', { p_connection_id: id });
      if (error) throw error;
      if (data.error) { showError(t('buddies.errorGeneric')); return; }
      showSuccess(t('buddies.accepted'));
      await load();
    } catch (e) { showError(e.message); }
  }

  async function handleReject(id) {
    if (!confirm(t('buddies.rejectConfirm'))) return;
    try {
      const { error } = await supabase.rpc('reject_buddy_request', { p_connection_id: id });
      if (error) throw error;
      await load();
    } catch (e) { showError(e.message); }
  }

  async function handleRemove(id) {
    if (!confirm(t('buddies.removeConfirm'))) return;
    try {
      const { error } = await supabase.rpc('remove_buddy', { p_connection_id: id });
      if (error) throw error;
      await load();
    } catch (e) { showError(e.message); }
  }

  if (loading) return <div className="page-loading">{t('common.laden')}</div>;

  return (
    <div className="page-container" style={{ maxWidth: 600 }}>
      <h1>{t('buddies.title')}</h1>

      {/* Eigen code */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3>{t('buddies.myCode')}</h3>
        <p style={{ fontSize: 13, color: '#666' }}>{t('buddies.myCodeDesc')}</p>
        <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 4, textAlign: 'center', padding: '12px 0', background: '#f5f5f5', borderRadius: 8 }}>
          {buddyCode}
        </div>
      </div>

      {/* Code invoeren */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3>{t('buddies.addBuddy')}</h3>
        <p style={{ fontSize: 13, color: '#666' }}>{t('buddies.addBuddyDesc')}</p>
        <form onSubmit={handleRequest} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            className="input"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="A1B2C3D4E5F6A7B8"
            maxLength={16}
            style={{ textAlign: 'center', fontSize: 18, letterSpacing: 3, fontWeight: 700, flex: 1 }}
          />
          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? t('common.sending') : t('common.send')}
          </button>
        </form>
      </div>

      {/* Inkomende verzoeken */}
      {incoming.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3>{t('buddies.incoming')}</h3>
          {incoming.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span>{buddyName(c)}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm btn-success" onClick={() => handleAccept(c.id)}>✓</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleReject(c.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uitgaande */}
      {outgoing.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3>{t('buddies.outgoing')}</h3>
          {outgoing.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <span>{buddyName(c)}</span>
              <span style={{ fontSize: 12, color: '#999' }}>{t('buddies.pending')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Buddy's */}
      <div className="card">
        <h3>{t('buddies.myBuddies')}</h3>
        {accepted.length === 0 ? (
          <p style={{ color: '#999' }}>{t('buddies.noBuddies')}</p>
        ) : (
          accepted.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span>{buddyName(c)}</span>
              <button className="btn btn-sm btn-danger" onClick={() => handleRemove(c.id)}>🗑</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
